import React, { useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';

const DEFAULTS = { inquiries: '80', missed: '20', reply: '40', request: '50', confirm: '60', jobValue: '350' };

const FIELDS = [
  { key: 'inquiries', label: 'New Inquiries Per Month', unit: 'Leads', max: 100000, hint: 'Calls, form fills, and texts from new customers.' },
  { key: 'missed', label: 'Share Without Timely Follow-Up', unit: '%', max: 100, hint: 'Inquiries that are missed or not followed up the same day.' },
  { key: 'reply', label: 'Reply Rate To Follow-Up', unit: '%', max: 100, hint: 'Of those followed up, how many reply.' },
  { key: 'request', label: 'Replies That Request A Visit', unit: '%', max: 100, hint: 'Of replies, how many ask for an appointment.' },
  { key: 'confirm', label: 'Requests Your Team Confirms', unit: '%', max: 100, hint: 'Of requests, how many become confirmed bookings.' },
  { key: 'jobValue', label: 'Average Job Value', unit: 'USD', max: 1000000, hint: 'Your typical invoice for this kind of job.' },
];

const parse = (v, max) => {
  if (v === '' || v === '-') return { n: 0, err: 'Enter A Number' };
  const n = Number(v);
  if (!Number.isFinite(n)) return { n: 0, err: 'Enter A Number' };
  if (n < 0) return { n: 0, err: 'Must Be 0 Or More' };
  if (n > max) return { n: max, err: `Max ${max.toLocaleString('en-US')}` };
  return { n, err: null };
};
const fmt = (n) => (Math.round(n * 10) / 10).toLocaleString('en-US');
const usd = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const useEstimate = (vals) => useMemo(() => {
  const p = Object.fromEntries(FIELDS.map((f) => [f.key, parse(vals[f.key], f.max)]));
  const leads = p.inquiries.n * (p.missed.n / 100);
  const replies = leads * (p.reply.n / 100);
  const requests = replies * (p.request.n / 100);
  const bookings = requests * (p.confirm.n / 100);
  return { p, leads, replies, requests, bookings, value: bookings * p.jobValue.n };
}, [vals]);

const EstimatorInput = ({ f, value, err, onChange }) => (
  <div className="yv-est-field">
    <label htmlFor={`est-${f.key}`} className="yv-est-label">{f.label}</label>
    <div className={`yv-est-input-wrap ${err ? 'has-error' : ''}`}>
      <input
        id={`est-${f.key}`} type="number" inputMode="decimal" min="0" max={f.max} step="any"
        value={value} onChange={onChange} className="yv-est-input"
        aria-invalid={!!err} aria-describedby={`est-${f.key}-hint`} data-testid={`est-input-${f.key}`}
      />
      <span className="yv-est-unit" aria-hidden="true">{f.unit}</span>
    </div>
    <p id={`est-${f.key}-hint`} className={err ? 'yv-est-err' : 'yv-est-hint'} data-testid={err ? `est-error-${f.key}` : undefined}>{err || f.hint}</p>
  </div>
);

export const OpportunityEstimator = () => {
  const [vals, setVals] = useState(DEFAULTS);
  const r = useEstimate(vals);
  const edited = JSON.stringify(vals) !== JSON.stringify(DEFAULTS);
  const set = (k) => (e) => setVals((v) => ({ ...v, [k]: e.target.value }));

  return (
    <section id="estimator" className="yv-section yv-dark" data-testid="estimator-section">
      <div className="yv-container">
        <div className="yv-section-head yv-split-head">
          <div>
            <span className="yv-tag yv-tag-dark">Estimate Only · Your Assumptions</span>
            <h2 className="yv-h2 mt-5">Estimate The Opportunity <span className="yv-blue-ink">In Your Own Numbers.</span></h2>
          </div>
          <p className="yv-lede">Every Assumption Below Is Editable. Nothing Here Is A Benchmark, A Prediction, Or A Result From Another Business.</p>
        </div>
        <div className="yv-est" data-testid="estimator">
          <div className="yv-est-inputs">
            <div className="yv-est-bar">
              <span className="yv-mono" data-testid="est-assumption-note">{edited ? 'Using Your Assumptions' : 'Example Assumptions · Replace With Yours'}</span>
              <button className="yv-sim-btn" onClick={() => setVals(DEFAULTS)} data-testid="est-reset-btn"><RotateCcw className="w-4 h-4" /><span>Reset</span></button>
            </div>
            <div className="yv-est-grid">
              {FIELDS.map((f) => <EstimatorInput key={f.key} f={f} value={vals[f.key]} err={r.p[f.key].err} onChange={set(f.key)} />)}
            </div>
          </div>
          <div className="yv-est-out" aria-live="polite">
            <dl className="yv-est-funnel">
              <div><dt>Leads Without Timely Follow-Up</dt><dd data-testid="est-out-leads">{fmt(r.leads)}</dd></div>
              <div><dt>Estimated Replies</dt><dd data-testid="est-out-replies">{fmt(r.replies)}</dd></div>
              <div><dt>Estimated Appointment Requests</dt><dd data-testid="est-out-requests">{fmt(r.requests)}</dd></div>
              <div><dt>Estimated Confirmed Bookings</dt><dd data-testid="est-out-bookings">{fmt(r.bookings)}</dd></div>
            </dl>
            <div className="yv-est-value" data-testid="est-out-value-box">
              <div className="yv-est-value-label">Estimated Monthly Opportunity</div>
              <div className="yv-est-value-num yv-mono" data-testid="est-out-value">{usd(r.value)}</div>
              <p>Confirmed Bookings × Average Job Value. This Is Potential Work, Not Money In The Bank.</p>
            </div>
            <div className="yv-est-distinct">
              <div data-testid="est-revenue-note"><b>Revenue Collected</b><span>Not Estimated Here. Measured Only From Completed, Paid Jobs.</span></div>
              <div data-testid="est-profit-note"><b>Profit</b><span>Not Estimated Here. Depends On Your Labor, Parts, And Overhead.</span></div>
            </div>
            <p className="yv-est-caveat" data-testid="est-caveat">
              Illustrative Calculation Only. Real Outcomes Depend On Your Lead Volume, How Quickly Your Team
              Responds, Customer Demand, Seasonality, And Pricing. YOVAI Does Not Guarantee Replies, Bookings, Or Revenue.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
